import React, { useMemo, useState, Component } from 'react';
import { SupplierLayout } from '../../components/SupplierLayout';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Download, FileText, FileSpreadsheet, Truck, Briefcase, Gavel, Users, Wallet, ShieldCheck, Calendar, ChevronDown, Clock, Search, Sparkles } from 'lucide-react';
type ReportFormat = 'pdf' | 'csv' | 'xlsx';
type ReportDef = {
  id: string;
  name: string;
  description: string;
  formats: ReportFormat[];
  updatedAt: string; // ISO
  rows?: number;
};
type ReportCategory = {
  id: string;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  accent: string; // tailwind text + bg accent
  reports: ReportDef[];
};
const CATEGORIES: ReportCategory[] = [{
  id: 'fleet',
  label: 'Fleet',
  icon: Truck,
  accent: 'text-amber-700 bg-amber-50 ring-amber-200',
  reports: [{
    id: 'fleet-utilization',
    name: 'Fleet Utilization',
    description: 'Hours active, idle time, and jobs per truck across the selected period.',
    formats: ['pdf', 'csv', 'xlsx'],
    updatedAt: '2026-06-02T14:30:00Z',
    rows: 24
  }, {
    id: 'fleet-inventory',
    name: 'Truck Inventory',
    description: 'Full list of trucks with plate, type, capacity, and assignment status.',
    formats: ['pdf', 'csv'],
    updatedAt: '2026-06-01T09:10:00Z',
    rows: 24
  }, {
    id: 'fleet-mileage',
    name: 'Mileage & Fuel',
    description: 'Distance covered and estimated fuel consumption per truck.',
    formats: ['csv', 'xlsx'],
    updatedAt: '2026-05-30T18:45:00Z',
    rows: 24
  }]
}, {
  id: 'jobs',
  label: 'Jobs',
  icon: Briefcase,
  accent: 'text-orange-700 bg-orange-50 ring-orange-200',
  reports: [{
    id: 'jobs-summary',
    name: 'Jobs Summary',
    description: 'All jobs with status, service type, customer, route, and revenue.',
    formats: ['pdf', 'csv', 'xlsx'],
    updatedAt: '2026-06-03T07:00:00Z',
    rows: 184
  }, {
    id: 'jobs-completed',
    name: 'Completed Jobs',
    description: 'Closed jobs with ratings, on-time rate, and final invoice amounts.',
    formats: ['pdf', 'csv'],
    updatedAt: '2026-06-02T22:15:00Z',
    rows: 142
  }, {
    id: 'jobs-cancelled',
    name: 'Cancellations & No-shows',
    description: 'Cancelled jobs with reason codes and customer/driver attribution.',
    formats: ['csv', 'xlsx'],
    updatedAt: '2026-06-01T11:30:00Z',
    rows: 12
  }]
}, {
  id: 'bidding',
  label: 'Bidding',
  icon: Gavel,
  accent: 'text-emerald-700 bg-emerald-50 ring-emerald-200',
  reports: [{
    id: 'bids-history',
    name: 'Bid History',
    description: 'All submitted bids with amount, win/loss, response time, and margin.',
    formats: ['pdf', 'csv', 'xlsx'],
    updatedAt: '2026-06-03T06:45:00Z',
    rows: 96
  }, {
    id: 'bids-winrate',
    name: 'Win Rate Analysis',
    description: 'Conversion by service type, lane, and price band — find where you win.',
    formats: ['pdf', 'xlsx'],
    updatedAt: '2026-06-02T16:20:00Z',
    rows: 48
  }, {
    id: 'bids-lost',
    name: 'Lost Bids Breakdown',
    description: 'Bids you lost with winning price (where available) and gap analysis.',
    formats: ['csv'],
    updatedAt: '2026-05-29T13:10:00Z',
    rows: 31
  }]
}, {
  id: 'drivers',
  label: 'Drivers',
  icon: Users,
  accent: 'text-sky-700 bg-sky-50 ring-sky-200',
  reports: [{
    id: 'drivers-performance',
    name: 'Driver Performance',
    description: 'Jobs completed, average rating, on-time rate, and revenue per driver.',
    formats: ['pdf', 'csv', 'xlsx'],
    updatedAt: '2026-06-02T19:00:00Z',
    rows: 10
  }, {
    id: 'drivers-hours',
    name: 'Hours & Availability',
    description: 'Active hours, shifts, and availability windows across the team.',
    formats: ['csv', 'xlsx'],
    updatedAt: '2026-06-01T08:40:00Z',
    rows: 10
  }]
}, {
  id: 'finance',
  label: 'Finance',
  icon: Wallet,
  accent: 'text-violet-700 bg-violet-50 ring-violet-200',
  reports: [{
    id: 'finance-payouts',
    name: 'Payouts Statement',
    description: 'Settled payouts with job references, fees, and net amount transferred.',
    formats: ['pdf', 'csv', 'xlsx'],
    updatedAt: '2026-06-02T21:00:00Z',
    rows: 58
  }, {
    id: 'finance-vat',
    name: 'VAT / Tax Summary',
    description: 'Tax-ready summary of invoiced revenue, VAT collected, and adjustments.',
    formats: ['pdf', 'xlsx'],
    updatedAt: '2026-05-31T17:00:00Z',
    rows: 12
  }, {
    id: 'finance-receivables',
    name: 'Outstanding Receivables',
    description: 'Unpaid invoices and aging buckets (0–30, 31–60, 60+ days).',
    formats: ['csv', 'xlsx'],
    updatedAt: '2026-06-03T05:30:00Z',
    rows: 7
  }]
}, {
  id: 'compliance',
  label: 'Compliance',
  icon: ShieldCheck,
  accent: 'text-rose-700 bg-rose-50 ring-rose-200',
  reports: [{
    id: 'compliance-permits',
    name: 'Permits & Expiries',
    description: 'Active permits, RTA registrations, and upcoming expiry dates.',
    formats: ['pdf', 'csv'],
    updatedAt: '2026-06-01T12:00:00Z',
    rows: 18
  }, {
    id: 'compliance-incidents',
    name: 'Incidents & Disputes',
    description: 'Logged incidents, customer disputes, and resolution outcomes.',
    formats: ['pdf', 'csv'],
    updatedAt: '2026-05-28T10:15:00Z',
    rows: 4
  }]
}];
const ALL_REPORTS = CATEGORIES.flatMap(c => c.reports.map(r => ({
  ...r,
  categoryId: c.id,
  categoryLabel: c.label
})));
const DATE_RANGES = [{
  id: '7d',
  label: 'Last 7 days'
}, {
  id: '30d',
  label: 'Last 30 days'
}, {
  id: '90d',
  label: 'Last 90 days'
}, {
  id: 'qtd',
  label: 'Quarter to date'
}, {
  id: 'ytd',
  label: 'Year to date'
}, {
  id: 'custom',
  label: 'Custom range…'
}] as const;
type RangeId = (typeof DATE_RANGES)[number]['id'];
const FORMAT_META: Record<ReportFormat, {
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  tone: string;
}> = {
  pdf: {
    label: 'PDF',
    icon: FileText,
    tone: 'text-rose-700 bg-rose-50 ring-rose-200'
  },
  csv: {
    label: 'CSV',
    icon: FileSpreadsheet,
    tone: 'text-emerald-700 bg-emerald-50 ring-emerald-200'
  },
  xlsx: {
    label: 'XLSX',
    icon: FileSpreadsheet,
    tone: 'text-sky-700 bg-sky-50 ring-sky-200'
  }
};
function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });
}
export function SupplierReports() {
  const [range, setRange] = useState<RangeId>('30d');
  const [rangeOpen, setRangeOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<Record<string, ReportFormat>>({});
  const [recent, setRecent] = useState<{
    reportId: string;
    format: ReportFormat;
    at: number;
  }[]>([]);
  const formatFor = (r: ReportDef): ReportFormat => {
    const chosen = selectedFormats[r.id];
    if (chosen && r.formats.includes(chosen)) return chosen;
    // Default to CSV if available, else first available
    return r.formats.includes('csv') ? 'csv' : r.formats[0];
  };
  const rangeLabel = DATE_RANGES.find(r => r.id === range)?.label ?? '';
  const handleDownload = (reportId: string, format: ReportFormat) => {
    const r = ALL_REPORTS.find(x => x.id === reportId);
    if (!r) return;
    setRecent(prev => [{
      reportId,
      format,
      at: Date.now()
    }, ...prev.filter(p => !(p.reportId === reportId && p.format === format))].slice(0, 5));
    toast.success(`${r.name}.${format} is ready`, {
      description: `Generated for ${rangeLabel.toLowerCase()} — download started.`
    });
  };
  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATEGORIES;
    return CATEGORIES.map(c => ({
      ...c,
      reports: c.reports.filter(r => r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || c.label.toLowerCase().includes(q))
    })).filter(c => c.reports.length > 0);
  }, [query]);
  const totalReports = ALL_REPORTS.length;
  return <SupplierLayout data-id="element-4709">
      <div className="max-w-6xl mx-auto" data-id="element-4710">
        {/* Header */}
        <div className="mb-6" data-id="element-4711">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4" data-id="element-4712">
            <div data-id="element-4713">
              <h1 className="text-2xl font-display font-bold text-gray-900" data-id="element-4714">
                Reports
              </h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-4715">
                Download exports across fleet, jobs, bidding, drivers, finance,
                and compliance.
              </p>
            </div>

            {/* Date range */}
            <div className="relative" data-id="element-4716">
              <button onClick={() => setRangeOpen(v => !v)} className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4717">
                <Calendar className="w-4 h-4 text-gray-500" data-id="element-4718" />
                {rangeLabel}
                <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-4719" />
              </button>
              <AnimatePresence data-id="element-4720">
                {rangeOpen && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden" data-id="element-4721">
                    {DATE_RANGES.map(r => <button key={r.id} onClick={() => {
                  setRange(r.id);
                  setRangeOpen(false);
                }} className={`w-full text-left px-3.5 py-2 text-sm font-medium transition-colors ${range === r.id ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-4722">
                        {r.label}
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Search */}
          <div className="mt-5 flex items-center gap-3" data-id="element-4723">
            <div className="relative flex-1" data-id="element-4724">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4725" />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search reports — e.g. utilization, bids, VAT…" className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300" data-id="element-4726" />
            </div>
            <div className="hidden md:block text-xs text-gray-500 font-medium whitespace-nowrap" data-id="element-4727">
              {totalReports} reports available
            </div>
          </div>
        </div>

        {/* Recent downloads */}
        {recent.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-6 bg-white border border-gray-200 rounded-2xl p-4 shadow-sm" data-id="element-4728">
            <div className="flex items-center gap-2 mb-3" data-id="element-4729">
              <Sparkles className="w-4 h-4 text-orange-500" data-id="element-4730" />
              <h3 className="text-sm font-bold text-gray-900" data-id="element-4731">
                Recent downloads
              </h3>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-4732">
              {recent.map((r, i) => {
            const def = ALL_REPORTS.find(x => x.id === r.reportId);
            if (!def) return null;
            const meta = FORMAT_META[r.format];
            return <button key={`${r.reportId}-${r.format}-${i}`} onClick={() => handleDownload(r.reportId, r.format)} className="group inline-flex items-center gap-2 pl-2 pr-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full text-xs font-semibold text-gray-700 transition-colors" data-id="element-4733">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 ${meta.tone}`} data-id="element-4734">
                      <meta.icon className="w-3 h-3" data-id="element-4735" />
                      {meta.label}
                    </span>
                    <span className="text-gray-800" data-id="element-4736">{def.name}</span>
                    <Download className="w-3.5 h-3.5 text-gray-400 group-hover:text-orange-600" data-id="element-4737" />
                  </button>;
          })}
            </div>
          </motion.div>}

        {/* Categories */}
        <div className="space-y-8" data-id="element-4738">
          {filteredCategories.map(cat => {
          const CatIcon = cat.icon;
          return <section key={cat.id} data-id="element-4739">
                <div className="flex items-center gap-3 mb-3" data-id="element-4740">
                  <div className={`w-9 h-9 rounded-xl ring-1 flex items-center justify-center ${cat.accent}`} data-id="element-4741">
                    <CatIcon className="w-4.5 h-4.5" data-id="element-4742" />
                  </div>
                  <div data-id="element-4743">
                    <h2 className="font-bold text-gray-900" data-id="element-4744">{cat.label}</h2>
                    <p className="text-xs text-gray-500" data-id="element-4745">
                      {cat.reports.length} report
                      {cat.reports.length === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-4746">
                  {cat.reports.map(r => <motion.div key={r.id} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-4747">
                      <div className="flex items-start justify-between gap-3" data-id="element-4748">
                        <div data-id="element-4749">
                          <h3 className="font-bold text-gray-900 text-sm leading-tight" data-id="element-4750">
                            {r.name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed" data-id="element-4751">
                            {r.description}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center gap-3 text-[11px] text-gray-500" data-id="element-4752">
                        <span className="inline-flex items-center gap-1" data-id="element-4753">
                          <Clock className="w-3 h-3" data-id="element-4754" />
                          Updated {formatRelative(r.updatedAt)}
                        </span>
                        {r.rows != null && <>
                            <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-4755" />
                            <span className="font-medium" data-id="element-4756">
                              {r.rows.toLocaleString()} rows
                            </span>
                          </>}
                      </div>

                      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2" data-id="element-4757">
                        <div role="radiogroup" aria-label={`File format for ${r.name}`} className="inline-flex items-center gap-1 p-0.5 bg-gray-50 ring-1 ring-gray-200 rounded-lg" data-id="element-4758">
                          {r.formats.map(f => {
                      const meta = FORMAT_META[f];
                      const active = formatFor(r) === f;
                      return <button key={f} role="radio" aria-checked={active} onClick={() => setSelectedFormats(prev => ({
                        ...prev,
                        [r.id]: f
                      }))} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold transition-colors ${active ? 'bg-white text-gray-900 ring-1 ring-gray-200 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-4759">
                                <meta.icon className="w-3 h-3" data-id="element-4760" />
                                {meta.label}
                              </button>;
                    })}
                        </div>
                        <button onClick={() => handleDownload(r.id, formatFor(r))} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 hover:bg-black text-white rounded-lg text-xs font-bold transition-colors" data-id="element-4761">
                          <Download className="w-3.5 h-3.5" data-id="element-4762" />
                          Download {FORMAT_META[formatFor(r)].label}
                        </button>
                      </div>
                    </motion.div>)}
                </div>
              </section>;
        })}

          {filteredCategories.length === 0 && <div className="text-center py-16 bg-white border border-dashed border-gray-200 rounded-2xl" data-id="element-4763">
              <p className="text-sm font-semibold text-gray-700" data-id="element-4764">
                No reports match your search
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-4765">
                Try a different keyword.
              </p>
            </div>}
        </div>
      </div>
    </SupplierLayout>;
}